/**
 * Analytics events: the closed catalog of what the storefront and the platform record about
 * shopper behavior, and the wire shape the storefront sends them in. Every event's properties
 * are an allowlist (ids, route patterns, counts, status codes) checked by its schema, so a
 * field nobody listed can never reach the analytics table. No event carries cents (money comes
 * from the fact tables) and none carries a name, email, address or IP.
 *
 * Browser events are intents and UI states, sent by the storefront under the environment's
 * analytics mode; server events are outcomes, emitted by the public API when a mutation
 * succeeds and by core for the few outcomes no request drives. The split keeps a funnel honest:
 * `page_view → open_clicked → box_opened` counts each step once.
 */
import { z } from "zod";

import { BATTLE_MODES } from "./battles.js";
import { countryDisplayName } from "./countries.js";

// ---- analytics mode -------------------------------------------------------------

/**
 * Whether a storefront records browser events, per environment, set by platform staff.
 * `disabled`: nothing is sent and the ingest route refuses the environment. `consent_required`:
 * sent only after Termly's `analytics` consent, so a store with no cookie banner sends nothing.
 * `enabled`: sent without a consent signal. Server events are recorded in every mode.
 */
export const ANALYTICS_MODES = ["disabled", "consent_required", "enabled"] as const;
export type AnalyticsMode = (typeof ANALYTICS_MODES)[number];
export const analyticsModeSchema = z.enum(ANALYTICS_MODES);
export const DEFAULT_ANALYTICS_MODE: AnalyticsMode = "consent_required";

/** The dashboard's label per mode. */
export const ANALYTICS_MODE_LABELS: Record<AnalyticsMode, string> = {
  disabled: "Disabled",
  consent_required: "Consent required",
  enabled: "Enabled",
};

/** The storefront config's analytics block, resolved for the SPA. */
export const storefrontAnalyticsSchema = z.object({ mode: analyticsModeSchema });
export type StorefrontAnalytics = z.infer<typeof storefrontAnalyticsSchema>;

// ---- rollups ------------------------------------------------------------------------

/** The buckets the dashboard reads: hourly for a day's view, daily for longer windows. */
export const ANALYTICS_GRANULARITIES = ["hour", "day"] as const;
export type AnalyticsGranularity = (typeof ANALYTICS_GRANULARITIES)[number];
export const analyticsGranularitySchema = z.enum(ANALYTICS_GRANULARITIES);

/**
 * The event name of a bucket's totals across every event: distinct shoppers cannot be summed
 * across names, so Athena counts the whole bucket once more under this name and the timeseries
 * and summary read only these rows.
 */
export const ANALYTICS_ALL_EVENTS = "*";

/**
 * What an event name looks like, for a reader that must accept names the catalog no longer
 * lists: a stored row outlives its catalog entry and must stay findable. `*` never matches.
 */
export const ANALYTICS_EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** The most events one insight charts: one per `--chart-N` color the UI kit defines. */
export const ANALYTICS_SERIES_MAX_EVENTS = 5;

// ---- dimensions -----------------------------------------------------------------
// A dimension is a column stamped on the event when it is recorded, never derived when it is
// rolled up: the rollup groups by it in the same Athena scan, and a recompute of an old window
// answers what it answered the first time. Every value set is closed and coarse (an operating
// system family, not a version), so a dimension describes a crowd and never picks out a shopper.

/** The operating system family read from the user agent. */
export const ANALYTICS_OS_VALUES = [
  "ios",
  "android",
  "windows",
  "macos",
  "linux",
  "chromeos",
  "other",
] as const;
export type AnalyticsOs = (typeof ANALYTICS_OS_VALUES)[number];

/** The device class read from the user agent. */
export const ANALYTICS_DEVICE_VALUES = ["desktop", "mobile", "tablet", "other"] as const;
export type AnalyticsDevice = (typeof ANALYTICS_DEVICE_VALUES)[number];

/**
 * How much a shopper has spent lately, lowest first. `none` is a signed-in shopper who spent
 * nothing in the window; the thresholds are a code constant in core (`customerSegment.ts`).
 */
export const CUSTOMER_SEGMENTS = ["none", "minnow", "dolphin", "whale"] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];
export const customerSegmentSchema = z.enum(CUSTOMER_SEGMENTS);

/** The segment column's value for an event no signed-in shopper sent: there is no customer yet. */
export const ANALYTICS_SEGMENT_ANONYMOUS = "anonymous";

/**
 * What a breakdown calls an event whose column is empty: recorded before the dimension existed,
 * a server event sent without the browser's consent to its context, or a request no browser
 * made. It is a value like any other, so a breakdown's values always add up to the event's total.
 */
export const ANALYTICS_UNKNOWN_VALUE = "unknown";

export interface AnalyticsDimensionSpec {
  /** What the dashboard calls the dimension. */
  label: string;
  /** The record's column, which is also the Iceberg table's. */
  column: "os" | "device" | "country" | "segment";
}

/**
 * Every dimension an insight can be broken down by. To add one: a column in
 * `ANALYTICS_COLUMNS`, the code that stamps it in `toAnalyticsRecord`, and a row here; the
 * rollup query, the breakdown table and the Insights page read this list.
 */
export const ANALYTICS_DIMENSIONS = {
  os: { label: "Operating system", column: "os" },
  device: { label: "Device", column: "device" },
  country: { label: "Country", column: "country" },
  segment: { label: "Customer segment", column: "segment" },
} as const satisfies Record<string, AnalyticsDimensionSpec>;

export type AnalyticsDimension = keyof typeof ANALYTICS_DIMENSIONS;
export const ANALYTICS_DIMENSION_NAMES = Object.keys(ANALYTICS_DIMENSIONS) as AnalyticsDimension[];
export const analyticsDimensionSchema = z.enum(
  ANALYTICS_DIMENSION_NAMES as [AnalyticsDimension, ...AnalyticsDimension[]],
);

/** A stored breakdown value: a lowercase token or an ISO country code, never free text. */
export const ANALYTICS_DIMENSION_VALUE_PATTERN = /^[A-Za-z0-9_]{1,32}$/;

/** A viewer country as CloudFront names it: ISO 3166-1 alpha-2, upper case. */
export const ANALYTICS_COUNTRY_PATTERN = /^[A-Z]{2}$/;

const OS_LABELS: Record<AnalyticsOs, string> = {
  ios: "iOS",
  android: "Android",
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  chromeos: "ChromeOS",
  other: "Other",
};

const DEVICE_LABELS: Record<AnalyticsDevice, string> = {
  desktop: "Desktop",
  mobile: "Mobile",
  tablet: "Tablet",
  other: "Other",
};

const SEGMENT_LABELS: Record<CustomerSegment | typeof ANALYTICS_SEGMENT_ANONYMOUS, string> = {
  none: "No recent spend",
  minnow: "Minnow",
  dolphin: "Dolphin",
  whale: "Whale",
  anonymous: "Anonymous",
};

/** What the dashboard calls a breakdown value; a value no label knows is shown as stored. */
export function analyticsDimensionValueLabel(dimension: AnalyticsDimension, value: string): string {
  if (value === ANALYTICS_UNKNOWN_VALUE) return "Unknown";
  if (dimension === "country") {
    return ANALYTICS_COUNTRY_PATTERN.test(value) ? countryDisplayName(value) : value;
  }
  const labels: Record<string, string> =
    dimension === "os" ? OS_LABELS : dimension === "device" ? DEVICE_LABELS : SEGMENT_LABELS;
  return labels[value] ?? value;
}

/**
 * The request header a browser sets on its `/v1` calls while its analytics gate is open (the
 * environment's mode allows sending and, under `consent_required`, the shopper consented). Only
 * then may a server event carry the request's operating system, device and country; without it
 * those columns stay empty. Its one accepted value is `1`.
 */
export const ANALYTICS_CONTEXT_HEADER = "x-loot-analytics-context";

// ---- the catalog ----------------------------------------------------------------

export const ANALYTICS_SOURCES = ["browser", "server"] as const;
export type AnalyticsSource = (typeof ANALYTICS_SOURCES)[number];

/** A Loot OS record id (ULID or UUID) as a property value. */
const idProperty = z.string().min(1).max(64);
/** A route pattern (`/boxes/$boxId`), never a concrete path with a query string. */
const routeProperty = z
  .string()
  .min(1)
  .max(256)
  .regex(/^\/[^?#\s]*$/, "a route pattern without a query string");
/** A `LootErrorCode`, never an error message. */
const codeProperty = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, "an error code");
const quantityProperty = z.int().min(1).max(1000);

export interface AnalyticsEventSpec {
  source: AnalyticsSource;
  /** What the dashboard calls the event. */
  label: string;
  /** The allowlisted properties; strict, so an unlisted key is refused rather than dropped. */
  properties: z.ZodObject;
}

export const ANALYTICS_EVENTS = {
  // ---- browser: intents and UI states --------------------------------------------
  page_view: {
    source: "browser",
    label: "Page view",
    properties: z.strictObject({ route: routeProperty }),
  },
  open_clicked: {
    source: "browser",
    label: "Open clicked",
    properties: z.strictObject({
      boxId: idProperty,
      quantity: quantityProperty,
      /** The signed-out demo open, which reaches no server. */
      demo: z.boolean(),
    }),
  },
  open_failed: {
    source: "browser",
    label: "Open failed",
    properties: z.strictObject({ boxId: idProperty, code: codeProperty }),
  },
  insufficient_balance: {
    source: "browser",
    label: "Insufficient balance",
    properties: z.strictObject({
      boxId: idProperty.optional(),
      action: z.enum(["open", "battle_create", "battle_join"]),
    }),
  },
  claim_prompt_shown: {
    source: "browser",
    label: "Claim prompt shown",
    properties: z.strictObject({ kind: z.enum(["open_group", "battle"]) }),
  },
  battle_action_failed: {
    source: "browser",
    label: "Battle action failed",
    properties: z.strictObject({
      action: z.enum(["create", "join", "claim", "sell_back"]),
      code: codeProperty,
    }),
  },
  checkout_started: {
    source: "browser",
    label: "Checkout started",
    properties: z.strictObject({ packId: idProperty }),
  },
  checkout_returned: {
    source: "browser",
    label: "Checkout returned",
    properties: z.strictObject({ status: z.enum(["completed", "failed", "cancelled"]) }),
  },
  sign_in_requested: {
    source: "browser",
    label: "Sign-in requested",
    properties: z.strictObject({}),
  },
  fairness_verified: {
    source: "browser",
    label: "Fairness verified",
    properties: z.strictObject({ outcome: z.enum(["valid", "invalid", "error"]) }),
  },
  // ---- server: outcomes ------------------------------------------------------------
  box_opened: {
    source: "server",
    label: "Box opened",
    properties: z.strictObject({ boxId: idProperty, quantity: quantityProperty }),
  },
  reveal_claimed: {
    source: "server",
    label: "Reveal claimed",
    properties: z.strictObject({ openGroupId: idProperty }),
  },
  reveal_sold_back: {
    source: "server",
    label: "Reveal sold back",
    properties: z.strictObject({ openGroupId: idProperty }),
  },
  inventory_sold: {
    source: "server",
    label: "Inventory sold",
    properties: z.strictObject({ count: z.int().min(1) }),
  },
  battle_created: {
    source: "server",
    label: "Battle created",
    properties: z.strictObject({
      battleId: idProperty,
      mode: z.enum(BATTLE_MODES),
      playerCount: z.int().min(2).max(16),
    }),
  },
  battle_joined: {
    source: "server",
    label: "Battle joined",
    properties: z.strictObject({ battleId: idProperty }),
  },
  battle_completed: {
    source: "server",
    label: "Battle completed",
    properties: z.strictObject({ battleId: idProperty, isWinner: z.boolean() }),
  },
  battle_claimed: {
    source: "server",
    label: "Battle claimed",
    properties: z.strictObject({ battleId: idProperty }),
  },
  battle_sold_back: {
    source: "server",
    label: "Battle sold back",
    properties: z.strictObject({ battleId: idProperty }),
  },
  forge_box_created: {
    source: "server",
    label: "Forge box created",
    properties: z.strictObject({ boxId: idProperty, itemCount: z.int().min(1).max(500) }),
  },
  forge_box_updated: {
    source: "server",
    label: "Forge box updated",
    properties: z.strictObject({
      boxId: idProperty,
      /** A new drop table (and cut), or the status the owner set. */
      change: z.enum(["drop_table", "active", "archived", "deleted"]),
    }),
  },
  forge_earnings_claimed: {
    source: "server",
    label: "Forge earnings claimed",
    properties: z.strictObject({}),
  },
  kyc_started: {
    source: "server",
    label: "Identity verification started",
    properties: z.strictObject({}),
  },
  shipping_address_saved: {
    source: "server",
    label: "Shipping address saved",
    properties: z.strictObject({}),
  },
  order_created: {
    source: "server",
    label: "Order created",
    properties: z.strictObject({ orderId: idProperty }),
  },
  balance_purchase_completed: {
    source: "server",
    label: "Balance purchase completed",
    properties: z.strictObject({ packId: idProperty }),
  },
  signed_in: {
    source: "server",
    label: "Signed in",
    properties: z.strictObject({}),
  },
} as const satisfies Record<string, AnalyticsEventSpec>;

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;
export type AnalyticsEventProperties<N extends AnalyticsEventName> = z.infer<
  (typeof ANALYTICS_EVENTS)[N]["properties"]
>;

/** Every catalog name, in catalog order. */
export const ANALYTICS_EVENT_NAMES = Object.keys(ANALYTICS_EVENTS) as AnalyticsEventName[];

type NamesOf<S extends AnalyticsSource> = {
  [N in AnalyticsEventName]: (typeof ANALYTICS_EVENTS)[N]["source"] extends S ? N : never;
}[AnalyticsEventName];
export type BrowserAnalyticsEventName = NamesOf<"browser">;
export type ServerAnalyticsEventName = NamesOf<"server">;

/** The names the storefront may send; the ingest route refuses every other name. */
export const BROWSER_ANALYTICS_EVENT_NAMES = ANALYTICS_EVENT_NAMES.filter(
  (name): name is BrowserAnalyticsEventName => ANALYTICS_EVENTS[name].source === "browser",
);
export const browserAnalyticsEventNameSchema = z.enum(
  BROWSER_ANALYTICS_EVENT_NAMES as [BrowserAnalyticsEventName, ...BrowserAnalyticsEventName[]],
);

/** The catalog entry for a name, typed. */
export function analyticsEventSpec(name: AnalyticsEventName): AnalyticsEventSpec {
  return ANALYTICS_EVENTS[name];
}

/** What a catalog name looks like: what the dashboard shows when a name has no label. */
export function analyticsEventLabel(name: string): string {
  return (ANALYTICS_EVENTS as Record<string, AnalyticsEventSpec>)[name]?.label ?? name;
}

// ---- the wire shape: POST /v1/analytics/events ---------------------------------

/** The storefront sends at most this many events per request; the server refuses more. */
export const ANALYTICS_BATCH_MAX_EVENTS = 50;

/**
 * A browser-minted event id (`crypto.randomUUID()`) or a server ULID. One id is one logical
 * event however many times it is delivered: the rollups count distinct ids.
 */
export const analyticsEventIdSchema = z.string().regex(/^[0-9A-Za-z-]{20,36}$/);

/** A per-browser or per-tab identifier the storefront minted; opaque to the server. */
const clientIdSchema = z.string().min(8).max(64);

/**
 * One browser event as sent. `properties` is checked against the named event's schema, so the
 * batch is refused whole when any event carries a key its catalog entry does not list.
 */
export const analyticsEventInputSchema = z
  .object({
    id: analyticsEventIdSchema,
    name: browserAnalyticsEventNameSchema,
    /** When it happened in the browser, epoch milliseconds; the server clamps it. */
    occurredAt: z.int().nonnegative(),
    properties: z.record(z.string(), z.unknown()).default({}),
  })
  .superRefine((event, ctx) => {
    const result = ANALYTICS_EVENTS[event.name].properties.safeParse(event.properties);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({ ...issue, path: ["properties", ...issue.path] });
      }
    }
  });
export type AnalyticsEventInput = z.infer<typeof analyticsEventInputSchema>;

export const analyticsBatchSchema = z.object({
  anonymousId: clientIdSchema.optional(),
  sessionId: clientIdSchema.optional(),
  events: z.array(analyticsEventInputSchema).min(1).max(ANALYTICS_BATCH_MAX_EVENTS),
});
export type AnalyticsBatch = z.infer<typeof analyticsBatchSchema>;
